import type { Exercise } from '@fluent/types';
import { State } from 'ts-fsrs';
import { getLesson } from '../content/lessons.js';
import { getCourseTree, isPhrasesLessonId } from '../content/course.js';
import { cardKey } from './cardKey.js';

/**
 * Deck derivation — FLASHCARDS.md § Deck derivation.
 *
 * A deck entry is a vocab item the learner met in a COMPLETED lesson, mined
 * from `tap_pairs.pairs[]` and `phrase_intro` only. Dialogue lines are
 * deliberately excluded: conversation replay lines make bad flashcards.
 * Dedup is first-lesson-wins by normalized English text, matching the
 * curriculum order the words were taught in.
 *
 * Main course only: phrases-course lessons (PHRASES.md § 3) never feed the
 * deck — their cards are idioms with their own review inside the course.
 */
export interface DeckEntry {
  key: string;
  en: string;
  ka: string;
  audio_key?: string;
  firstLessonId: string;
  firstLessonTitle: string;
}

export function extractDeckVocab(
  completedLessonIds: string[],
  lessonOrder: ReadonlyMap<string, number> = courseLessonOrder(),
): DeckEntry[] {
  // Curriculum order = the course tree's unit→skill→lesson walk. Lesson ids
  // are NOT zero-padded to one width ('lesson-97' vs 'lesson-113'), so a
  // lexicographic sort would interleave late review-skills (97–120) into
  // week-one vocabulary. Unknown ids sink to the end, then sort lexically
  // as a stable tiebreak.
  const sorted = completedLessonIds.filter((id) => !isPhrasesLessonId(id)).sort((a, b) => {
    const ia = lessonOrder.get(a) ?? Number.MAX_SAFE_INTEGER;
    const ib = lessonOrder.get(b) ?? Number.MAX_SAFE_INTEGER;
    if (ia !== ib) return ia - ib;
    return a < b ? -1 : a > b ? 1 : 0;
  });
  const seen = new Map<string, DeckEntry>();

  for (const lessonId of sorted) {
    const lesson = getLesson(lessonId);
    if (!lesson) continue;
    for (const ex of lesson.exercises) {
      for (const pair of pairsOf(ex)) {
        const key = cardKey(pair.en);
        if (seen.has(key)) continue;
        seen.set(key, {
          key,
          en: pair.en,
          ka: pair.ka,
          audio_key: pair.audio_key,
          firstLessonId: lesson.id,
          firstLessonTitle: lesson.title,
        });
      }
    }
  }

  return [...seen.values()];
}

function pairsOf(ex: Exercise): Array<{ en: string; ka: string; audio_key?: string }> {
  if (ex.type === 'tap_pairs') return ex.pairs;
  if (ex.type === 'phrase_intro') return [{ en: ex.en, ka: ex.ka, audio_key: ex.audio_key }];
  return [];
}

let cachedOrder: Map<string, number> | null = null;

/** lessonId → curriculum index from the course tree (cached; content is static). */
function courseLessonOrder(): Map<string, number> {
  if (!cachedOrder) {
    cachedOrder = new Map();
    const tree = getCourseTree();
    (tree?.orderedLessonIds ?? []).forEach((id, i) => cachedOrder!.set(id, i));
  }
  return cachedOrder;
}

/** Parsed `user_flashcards` row (dates as Date, DB naming preserved). */
export interface CardStateRow {
  card_key: string;
  direction: number;
  due: Date;
  stability: number;
  difficulty: number;
  scheduled_days: number;
  learning_steps: number;
  reps: number;
  lapses: number;
  state: State;
  last_review: Date | null;
  suspended: boolean;
  created_at: Date;
}

/** A playable card: deck entry + its scheduling state (null = never seen). */
export interface DeckCard {
  entry: DeckEntry;
  state: CardStateRow | null;
}

export interface DeckCounts {
  total: number;
  newCount: number;
  learning: number;
  review: number;
}

export interface SessionPlan {
  /** Due review cards, most at-risk first, capped at `reviewCap`. */
  due: DeckCard[];
  /** Never-seen cards, curriculum order, respecting today's new-card budget. */
  fresh: DeckCard[];
  counts: DeckCounts;
  /** Earliest future due among cards NOT in this plan (null = nothing scheduled). */
  nextDueAt: Date | null;
  /** How many due cards were deferred past the cap (never shown to the user). */
  deferred: number;
}

export const REVIEW_CAP = 20;

/**
 * Build today's session plan.
 *
 * - Overdue-first by *relative* overdueness (elapsed/scheduled) so the cards
 *   most at risk of being forgotten come first; hard-capped at REVIEW_CAP so
 *   a comeback after a break is a normal-sized session, never a wall of debt.
 * - New cards enter in curriculum order, limited to what's left of today's
 *   new-card budget (`newPerDay` minus cards first-seen today).
 * - The true backlog size is returned as `deferred` for diagnostics but the
 *   UI never surfaces it (FLASHCARDS.md § anti–review-debt).
 */
export function buildSessionPlan(
  entries: DeckEntry[],
  states: Map<string, CardStateRow>,
  opts: { now: Date; newPerDay: number; introducedToday: number },
): SessionPlan {
  const { now, newPerDay, introducedToday } = opts;

  const due: Array<{ card: DeckCard; overdueness: number }> = [];
  const fresh: DeckCard[] = [];
  const counts: DeckCounts = { total: 0, newCount: 0, learning: 0, review: 0 };
  let nextDueAt: Date | null = null;

  for (const entry of entries) {
    const state = states.get(entry.key) ?? null;
    counts.total += 1;

    if (!state) {
      counts.newCount += 1;
      fresh.push({ entry, state });
      continue;
    }
    if (state.suspended) continue;

    if (state.state === State.Learning || state.state === State.Relearning) counts.learning += 1;
    else if (state.state === State.Review) counts.review += 1;
    else counts.newCount += 1;

    if (state.due.getTime() <= now.getTime()) {
      const elapsedMs = now.getTime() - state.due.getTime();
      const scheduledMs = Math.max(state.scheduled_days, 1) * 86_400_000;
      due.push({ card: { entry, state }, overdueness: elapsedMs / scheduledMs });
    } else if (!nextDueAt || state.due < nextDueAt) {
      nextDueAt = state.due;
    }
  }

  due.sort((a, b) => b.overdueness - a.overdueness);
  const kept = due.slice(0, REVIEW_CAP).map((d) => d.card);

  const newBudget = Math.max(0, newPerDay - introducedToday);
  const freshKept = fresh.slice(0, newBudget);

  return {
    due: kept,
    fresh: freshKept,
    counts,
    nextDueAt,
    deferred: due.length - kept.length,
  };
}

/**
 * Cards whose state row was created today (user-local) = today's introduced
 * new cards. Uses the row's created_at, which is stamped on first review.
 */
export function countIntroducedToday(
  states: Iterable<CardStateRow>,
  now: Date,
  timeZone: string,
): number {
  const today = dayInTz(now, timeZone);
  let n = 0;
  for (const s of states) {
    if (dayInTz(s.created_at, timeZone) === today) n += 1;
  }
  return n;
}

function dayInTz(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}
